import React, { useCallback } from "react";
import { graphql } from "react-relay";
import { Link } from "react-router-dom";

import type { CheckedState } from "@radix-ui/react-checkbox";
import cx from "classnames";
import { Loader } from "semantic-ui-react";
import { v4 as uuid } from "uuid";

import AppContext from "~/common/AppContext";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import { withSuspense } from "~/lib/suspense";
import { isTeams } from "~/lib/utils";
import { Checkbox } from "~/ui";
import ArrowDropdown, { DropdownItem as ArrowDropdownItem } from "~/ui/ArrowDropdown";
import { TextArea, TextField } from "~/ui/legacyFellowIndex";

import { showFeatureModal } from "../billing";
import { type DocumentJSON, getTemplateContentEditorConfig, NoteTemplateContent } from "./content";
import { createTemplate, saveTemplate } from "./noteTemplateGql";

import type { EditNoteTemplateQuery } from "~/__generated__/EditNoteTemplateQuery.graphql";
import type { EditVisibilitySelectorQuery } from "~/__generated__/EditVisibilitySelectorQuery.graphql";
import type {
	noteTemplateGqlSaveTemplateMutation,
	TemplateVisibility,
} from "~/__generated__/noteTemplateGqlSaveTemplateMutation.graphql";

import styles from "./css/Edit.module.css";

function replaceCategoryTag(tags: readonly string[], category: Category | undefined) {
	const withoutCategory = tags.filter(t => !CATEGORIES.includes(t));
	if (category) return withoutCategory.concat([category]);
	return withoutCategory;
}

function findCategoryInTags(tags: readonly string[]): Category | undefined {
	return tags.find(t => CATEGORIES.includes(t));
}

function useStateWithListener<S>(
	initialState: S | (() => S),
	listener?: () => void,
): [S, React.Dispatch<React.SetStateAction<S>>] {
	const [state, setState] = React.useState(initialState);
	const debouncedFirst = React.useRef(false);

	React.useEffect(() => {
		if (!debouncedFirst.current) {
			debouncedFirst.current = true;
		} else {
			listener?.();
		}
	}, [listener, state]);

	return [state, setState];
}

export const NoteTemplateQuery = graphql`
	query EditNoteTemplateQuery($templateId: ID!) {
		node(id: $templateId) {
			... on NoteTemplateNode {
				id
				title
				summary
				tags
				proseDoc
				visibility
				schemaVersion
			}
		}

		account {
			billingAccount {
				hasAccessToPromptHeadings: hasAccessToFeature(feature: prompt_headings)
			}
		}
	}
`;

export const TemplateEdit = withSuspense(<Loader active />, TemplateEditInner);

function TemplateEditInner({
	formId,
	templateId,
	beforeSubmit,
	afterSubmit,
	onChange,
	className,
	canEditVisibility,
}: {
	formId: string;
	templateId: string;
	className?: string;
	canEditVisibility?: boolean;
	beforeSubmit?: () => void;
	afterSubmit?: (
		template: NonNullable<noteTemplateGqlSaveTemplateMutation["response"]["saveNoteTemplate"]>["template"] | null,
	) => void;
	onChange?: () => void;
}) {
	const props = useLazyLoadQuery<EditNoteTemplateQuery>(
		NoteTemplateQuery,
		{ templateId },
		{ fetchPolicy: "network-only" },
	);

	const onFormSubmit = useCallback(
		async (data: TemplateFormData) => {
			return await saveTemplate({
				id: templateId,
				...data,
			});
		},
		[templateId],
	);

	if (!props.node) throw new Error("Invalid template ID");

	return (
		<TemplateForm
			mode="edit"
			formId={formId}
			beforeSubmit={beforeSubmit}
			onSubmit={onFormSubmit}
			afterSubmit={afterSubmit}
			onChange={onChange}
			className={className}
			canEditVisibility={canEditVisibility}
			initialTitle={props.node.title ?? ""}
			initialDescription={props.node.summary}
			initialVisibility={props.node.visibility}
			initialTags={props.node.tags ?? []}
			initialDocument={props.node.proseDoc}
			hasAccessToPromptHeadings={props.account.billingAccount.hasAccessToPromptHeadings}
			schemaVersion={props.node.schemaVersion || 1}
		/>
	);
}

export function TemplateCreate({
	formId,
	beforeSubmit,
	afterSubmit,
	onChange,
	initialDoc = emptyDoc,
	initialTitle,
	initialDescription,
	initialTags,
	className,
	initialVisibility = "USER",
	canEditVisibility = false,
	parentId,
	hasAccessToPromptHeadings,
	schemaVersion,
}: {
	formId: string;
	beforeSubmit?: () => void;
	afterSubmit?: (id: string | null) => void;
	onChange?: () => void;
	initialDoc?: DocumentJSON;
	initialTitle?: string;
	initialDescription?: string;
	initialTags?: readonly string[];
	initialVisibility?: TemplateVisibility;
	canEditVisibility?: boolean;
	className?: string;
	parentId?: string;
	hasAccessToPromptHeadings: boolean;
	schemaVersion: number;
}) {
	return (
		<TemplateForm<string>
			mode="create"
			formId={formId}
			className={className}
			onChange={onChange}
			beforeSubmit={beforeSubmit}
			onSubmit={createTemplate}
			afterSubmit={afterSubmit}
			initialTitle={initialTitle ?? ""}
			initialDescription={initialDescription}
			initialDocument={initialDoc}
			initialTags={initialTags}
			canEditVisibility={canEditVisibility}
			initialVisibility={initialVisibility}
			parentId={parentId}
			hasAccessToPromptHeadings={hasAccessToPromptHeadings}
			schemaVersion={schemaVersion}
		/>
	);
}
export const TemplateCopy = withSuspense(<Loader active />, TemplateCopyInner);
function TemplateCopyInner({
	formId,
	templateId,
	beforeSubmit,
	afterSubmit,
	onChange,
	className,
	canEditVisibility = false,
	parentId,
}: {
	formId: string;
	templateId: string;
	beforeSubmit?: () => void;
	afterSubmit?: (id: string | null) => void;
	onChange?: () => void;
	canEditVisibility?: boolean;
	className?: string;
	parentId?: string;
}) {
	const props = useLazyLoadQuery<EditNoteTemplateQuery>(
		NoteTemplateQuery,
		{ templateId },
		{ fetchPolicy: "network-only" },
	);

	if (!props.node) throw new Error("Invalid template ID");

	return (
		<TemplateForm<string>
			mode="create"
			formId={formId}
			className={className}
			onChange={onChange}
			beforeSubmit={beforeSubmit}
			onSubmit={createTemplate}
			afterSubmit={afterSubmit}
			initialTitle={`(Copy) ${props.node.title}`}
			initialDescription={props.node.summary}
			initialVisibility="USER"
			initialTags={props.node.tags ?? []}
			initialDocument={props.node.proseDoc}
			canEditVisibility={canEditVisibility}
			parentId={parentId}
			hasAccessToPromptHeadings={props.account.billingAccount.hasAccessToPromptHeadings}
			schemaVersion={props.node.schemaVersion || 1}
		/>
	);
}

interface TemplateFormData {
	title: string;
	summary: string;
	visibility: TemplateVisibility;
	tags: string[];
	rawDraftJson: DocumentJSON;
	parentId?: string;
	schemaVersion: number;
}

export function TemplateForm<TSubmit>({
	mode,
	formId,
	onSubmit: onSubmit_,
	beforeSubmit,
	afterSubmit,
	onChange,
	className,
	canEditVisibility = false,
	initialTitle,
	initialDescription,
	initialVisibility,
	initialTags,
	initialDocument,
	parentId,
	hasAccessToPromptHeadings,
	schemaVersion,
}: {
	mode: "create" | "edit";
	formId: string;
	className?: string;
	canEditVisibility?: boolean;
	onSubmit: (data: TemplateFormData) => Promise<TSubmit>;
	beforeSubmit?: () => void;
	afterSubmit?: (value: TSubmit | null) => void;
	onChange?: () => void;
	initialTitle: string;
	initialDescription?: string;
	initialVisibility?: TemplateVisibility;
	initialDocument: DocumentJSON;
	initialTags?: readonly string[];
	parentId?: string;
	hasAccessToPromptHeadings: boolean;
	schemaVersion: number;
}) {
	const [title, setTitle] = useStateWithListener(initialTitle, onChange);
	const [description, setDescription] = useStateWithListener(initialDescription ?? "", onChange);
	const [visibility, setVisibility] = useStateWithListener<TemplateVisibility>(initialVisibility ?? "USER", onChange);
	const [category, setCategory] = useStateWithListener<Category | undefined>(
		() => findCategoryInTags(initialTags ?? []),
		onChange,
	);
	const [document, _setDocument] = React.useState<DocumentJSON>(initialDocument ?? emptyDoc);

	// since prose tells us the document has changed on mount
	const docFirstChange = React.useRef(true);
	const setDocument = React.useCallback(
		(document: DocumentJSON) => {
			if (!docFirstChange.current) {
				onChange?.();
			} else {
				docFirstChange.current = false;
			}
			_setDocument(document);
		},
		[onChange, _setDocument],
	);

	const onSubmit = useCallback(
		async (e: React.FormEvent<HTMLFormElement>) => {
			e.preventDefault();
			beforeSubmit?.();
			let val: TSubmit | null = null;
			try {
				val = await onSubmit_?.({
					title,
					summary: description,
					visibility,
					tags: replaceCategoryTag(initialTags ?? [], category),
					rawDraftJson: { doc: document },
					parentId: parentId,
					schemaVersion,
				});
			} finally {
				afterSubmit?.(val);
			}
		},
		[
			afterSubmit,
			beforeSubmit,
			category,
			description,
			document,
			initialTags,
			onSubmit_,
			parentId,
			title,
			visibility,
			schemaVersion,
		],
	);

	return (
		<TemplateEditor
			mode={mode}
			formId={formId}
			className={className}
			onSubmit={onSubmit}
			title={title}
			onTitleChange={setTitle}
			description={description}
			onDescriptionChange={setDescription}
			category={category}
			onCategoryChange={setCategory}
			document={document}
			onDocumentChange={setDocument}
			visibility={visibility}
			onVisibilityChange={canEditVisibility ? setVisibility : undefined}
			hasAccessToPromptHeadings={hasAccessToPromptHeadings}
			schemaVersion={schemaVersion}
		/>
	);
}

export const emptyDoc: DocumentJSON = {
	type: "doc",
	content: [
		{
			type: "unstyled",
			attrs: {
				assignees: [],
				creator: null,
				blockId: uuid(),
				indent: 0,
				done: false,
			},
		},
	],
};

interface TemplateEditorProps {
	formId: string;
	onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
	document: DocumentJSON;
	onDocumentChange?: (doc: DocumentJSON) => void;
	className?: string;
	hasAccessToPromptHeadings: boolean;
	schemaVersion: number;
}

type Props = MetadataFormProps & TemplateEditorProps;

function TemplateEditor({
	formId,
	onSubmit,
	document,
	onDocumentChange,
	className,
	hasAccessToPromptHeadings,
	schemaVersion,
	...metadataProps
}: Props) {
	const editableConfig = React.useMemo(() => getTemplateContentEditorConfig(schemaVersion, true), [schemaVersion]);
	return (
		<form id={formId} className={cx(styles.container, className)} onSubmit={onSubmit}>
			<MetadataForm className={styles.metaForm} {...metadataProps} />
			<NoteTemplateContent
				editorConfig={editableConfig}
				proseReactClassName={styles.proseReactEditor}
				containerClassName={styles.proseReactContainer}
				document={document}
				editable
				onChange={onDocumentChange}
				hasAccessToPromptHeadings={hasAccessToPromptHeadings}
			/>
		</form>
	);
}

const CATEGORIES: readonly string[] = ["1-on-1s", "Meetings", "Other", "Priorities"];
type Category = (typeof CATEGORIES)[number];

interface BaseMetadataFormProps {
	className?: string;

	title?: string;
	description?: string;
	category?: Category;
	onTitleChange?: (title: string) => void;
	onDescriptionChange?: (description: string) => void;
	onCategoryChange?: (category: Category | undefined) => void;
	mode: "edit" | "create";
}

type VisibilityProps = {
	visibility: TemplateVisibility;
	onVisibilityChange?: (visibility: TemplateVisibility) => void;
};
type MetadataFormProps = BaseMetadataFormProps & VisibilityProps;

const VisibilitySelectorInner: React.FC<VisibilityProps> = ({ visibility, onVisibilityChange }) => {
	const props = useLazyLoadQuery<EditVisibilitySelectorQuery>(
		graphql`
			query EditVisibilitySelectorQuery {
				viewer {
					account {
						billingAccount {
							hasAccessToTemplates: hasAccessToFeature(feature: templates)
						}
					}
					...BillingIdentityFeaturesPaywall_viewer
				}
			}
		`,
		{},
		{ fetchPolicy: "store-and-network" },
	);
	const paywalled = !props.viewer.account.billingAccount.hasAccessToTemplates;
	const onCheckedChange = React.useCallback(
		(checked: CheckedState) => {
			if (checked === "indeterminate") return; // should not happen
			if (checked) {
				if (paywalled) {
					showFeatureModal("templates");
					return;
				}
				onVisibilityChange && onVisibilityChange("WORKSPACE");
			} else {
				onVisibilityChange && onVisibilityChange("USER");
			}
		},
		[onVisibilityChange, paywalled],
	);
	return (
		<label className={styles.visibilityCheckbox}>
			<Checkbox checked={visibility === "WORKSPACE"} onCheckedChange={onCheckedChange} />
			<p>Allow workspace teammates to use this template</p>
		</label>
	);
};

export const VisibilitySelector = withSuspense(<Loader active inline="centered" />, VisibilitySelectorInner);

function MetadataForm(props: MetadataFormProps) {
	const { user } = React.useContext(AppContext);

	return (
		<div className={cx(styles.formContainer, props.className)}>
			<label className={styles.label}>
				<p className={styles.labelText}>Template title:</p>
				<TextField
					// Database length limit
					maxLength={128}
					required
					name="title"
					className={styles.control}
					value={props.title ?? ""}
					setValue={props.onTitleChange}
					placeholder="Untitled template"
					data-test-id="note-template-metadata-title"
				/>
			</label>
			<label className={styles.label}>
				<p className={styles.labelText}>Description:</p>
				<TextArea
					name="description"
					className={styles.textArea}
					// eslint-disable-next-line react/jsx-no-bind
					onChange={e => e.target instanceof HTMLTextAreaElement && props.onDescriptionChange?.(e.target.value)}
					placeholder="Set a description for your template…"
					value={props.description}
					data-test-id="note-template-metadata-description"
				/>
			</label>
			<label className={styles.label}>
				<p className={styles.labelText}>Category:</p>
				<ArrowDropdown
					text={props.category ?? <span className={styles.arrowDropdownPlaceholder}>Select a category</span>}
					heading="Category"
					className={styles.control}
					data-test-id="note-template-metadata-category"
				>
					<ArrowDropdownItem
						// eslint-disable-next-line react/jsx-no-bind
						onClick={() => props.onCategoryChange?.(undefined)}
					>
						No category
					</ArrowDropdownItem>
					{CATEGORIES.map(category => (
						<ArrowDropdownItem
							key={category}
							// eslint-disable-next-line react/jsx-no-bind
							onClick={() => props.onCategoryChange?.(category)}
							selected={category === props.category}
							data-test-id="note-template-metadata-category-option"
						>
							{category}
						</ArrowDropdownItem>
					))}
				</ArrowDropdown>
			</label>
			<label className={styles.label}>
				<p className={styles.labelText}>Visibility:</p>
				{props.onVisibilityChange ? (
					<VisibilitySelector visibility={props.visibility} onVisibilityChange={props.onVisibilityChange} />
				) : props.visibility === "WORKSPACE" ? (
					<p className={styles.control}>
						This template {props.mode === "create" ? "will be" : "is"} visible to your entire workspace.
					</p>
				) : (
					<>
						<p className={styles.control}>
							This template {props.mode === "create" ? "will only be" : "is only"} visible to you.
						</p>
						<aside className={styles.visibilityNote}>
							<b>Note: </b>
							{user?.internalAttributes?.isAccountAdmin ? (
								<>
									You can create and manage Workspace templates visible to all teammates from the{" "}
									<Link to="/templates-section/notes/" target={isTeams ? "_blank" : undefined}>
										Templates section
									</Link>
									.
								</>
							) : (
								<>To create templates visible to your entire workspace, you must be a workspace admin.</>
							)}
						</aside>
					</>
				)}
			</label>
		</div>
	);
}
